/**
 * Relatórios do backend novo (Fase 3, doc 07).
 *
 * Cada função tenta a rota /v1/relatorios e, se ela falhar, cai na RPC antiga
 * do PostgREST — com a falha registrada (FALHA_FRONT) para ninguém depender do
 * caminho antigo, que sai quando a migração da leitura terminar. As linhas têm
 * as mesmas colunas das RPCs: as telas não mudam o mapeamento.
 */

import { postgrestClient } from "../lib/postgrest/client";
import { apiServiceClient } from "../config/api";
import { registrarFalha } from "../lib/telemetria";

async function getV1(caminho, parametros) {
  const params = new URLSearchParams();
  Object.entries(parametros).forEach(([chave, valor]) => {
    if (valor !== null && valor !== undefined && valor !== "") params.set(chave, String(valor));
  });
  const query = params.toString();
  const response = await apiServiceClient.request(`${caminho}${query ? `?${query}` : ""}`, { method: "GET" });
  return response?.data || response;
}

function lista(rows) {
  return Array.isArray(rows) ? rows : [];
}

export const relatoriosService = {
  /**
   * CPFs, datas de nascimento e documentos com problema, numa só chamada (painel do dashboard).
   * @param {string|null} nucleoId
   * @returns {Promise<{cpfs: Array, datas_nascimento: Array, documentos: Array}>}
   */
  async qualidadeCadastro(nucleoId = null) {
    try {
      const dados = await getV1("/v1/relatorios/qualidade-cadastro", { nucleo_id: nucleoId });
      if (dados && Array.isArray(dados.cpfs) && Array.isArray(dados.datas_nascimento) && Array.isArray(dados.documentos)) {
        return dados;
      }
      throw new Error("Resposta inesperada de /v1/relatorios/qualidade-cadastro");
    } catch (erro) {
      registrarFalha("relatorios.qualidade_cadastro", erro);
    }

    const parametros = { p_tipo: "ambos", p_nucleo_id: nucleoId || null };
    const [cpfs, datas, documentos] = await Promise.all([
      postgrestClient.post("rpc/relatorio_cpfs_invalidos_duplicados", parametros),
      postgrestClient.post("rpc/relatorio_datas_nascimento_invalidas", parametros),
      postgrestClient.post("rpc/relatorio_documentos_invalidos", parametros),
    ]);
    return { cpfs: lista(cpfs), datas_nascimento: lista(datas), documentos: lista(documentos) };
  },

  /**
   * @param {string} tipo  ambos | invalidos | duplicados | certidoes
   * @param {string|null} nucleoId
   */
  async cpfsInvalidosDuplicados(tipo = "ambos", nucleoId = null) {
    try {
      const rows = await getV1("/v1/relatorios/cpfs-invalidos-duplicados", { tipo, nucleo_id: nucleoId });
      if (Array.isArray(rows)) return rows;
      throw new Error("Resposta inesperada de /v1/relatorios/cpfs-invalidos-duplicados");
    } catch (erro) {
      registrarFalha("relatorios.cpfs_invalidos_duplicados", erro);
    }
    return lista(await postgrestClient.post("rpc/relatorio_cpfs_invalidos_duplicados", {
      p_tipo: tipo,
      p_nucleo_id: nucleoId || null,
    }));
  },

  /**
   * @param {string} tipo  ambos | maior_18 | menor_6 | ausente
   * @param {string|null} nucleoId
   */
  async datasNascimentoInvalidas(tipo = "ambos", nucleoId = null) {
    try {
      const rows = await getV1("/v1/relatorios/datas-nascimento-invalidas", { tipo, nucleo_id: nucleoId });
      if (Array.isArray(rows)) return rows;
      throw new Error("Resposta inesperada de /v1/relatorios/datas-nascimento-invalidas");
    } catch (erro) {
      registrarFalha("relatorios.datas_nascimento_invalidas", erro);
    }
    return lista(await postgrestClient.post("rpc/relatorio_datas_nascimento_invalidas", {
      p_tipo: tipo,
      p_nucleo_id: nucleoId || null,
    }));
  },

  /**
   * Alunos Frequentes: alunos do núcleo com pelo menos `minimoPresencas` no
   * período; com minimoPresencas === 0, os que não tiveram nenhuma presença
   * (estes com responsável e telefone).
   * @param {{dataInicio: string, dataFim: string, nucleoId: string, negocioId: string, minimoPresencas: number|null}} filtros
   */
  async alunosFrequentes({ dataInicio, dataFim, nucleoId, negocioId, minimoPresencas = null }) {
    const semPresencas = minimoPresencas === 0;
    try {
      const rows = semPresencas
        ? await getV1("/v1/relatorios/alunos-sem-presencas", { nucleo_id: nucleoId, data_inicio: dataInicio, data_fim: dataFim })
        : await getV1("/v1/relatorios/alunos-frequentes", {
            nucleo_id: nucleoId, data_inicio: dataInicio, data_fim: dataFim, minimo_presencas: minimoPresencas,
          });
      if (Array.isArray(rows)) return rows;
      throw new Error("Resposta inesperada de /v1/relatorios/alunos-frequentes");
    } catch (erro) {
      // 403 é recusa de acesso, não falha do caminho novo: a RPC recusaria igual.
      if (erro?.status === 403) throw erro;
      registrarFalha(semPresencas ? "relatorios.alunos_sem_presencas" : "relatorios.alunos_frequentes", erro);
    }
    const parametros = { p_data_inicio: dataInicio, p_data_fim: dataFim, p_nucleo_id: nucleoId, p_negocio_id: negocioId };
    return lista(semPresencas
      ? await postgrestClient.post("rpc/relatorio_alunos_sem_presencas", parametros)
      : await postgrestClient.post("rpc/relatorio_alunos_ativos", { ...parametros, p_qtd_presencas: minimoPresencas ?? null }));
  },

  /**
   * Frequência por Aluno: vínculos de turma e chamadas do aluno no período, nas
   * turmas que o usuário vê no tenant do núcleo. Relatório só do backend novo,
   * sem RPC de fallback.
   * @param {{nucleoId: string, alunoId: string, dataInicio: string, dataFim: string}} filtros
   * @returns {Promise<{aluno_id, aluno_nome, turmas: Array, vinculos: Array, registros: Array}>}
   */
  async frequenciaAluno({ nucleoId, alunoId, dataInicio, dataFim }) {
    const dados = await getV1("/v1/relatorios/frequencia-aluno", {
      nucleo_id: nucleoId, aluno_id: alunoId, data_inicio: dataInicio, data_fim: dataFim,
    });
    if (!dados || !Array.isArray(dados.registros)) {
      throw new Error("Resposta inesperada de /v1/relatorios/frequencia-aluno");
    }
    return dados;
  },
};

export default relatoriosService;
